import type { AlertColor } from '@mui/material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Fade,
  Grid,
  Link,
  Paper,
  Typography,
  useTheme
} from '@mui/material';
import axios from 'axios';
import { useState } from 'react';

import settings from '@/data/constants';
import type {
  ConsultationResponse,
  ErrorResponse,
  ResponseData,
  SupportTasksProps
} from '@/types/support-self-service.types';
import { buildFormDataRequestBody, buildIdFromEndpoint, buildJsonRequestBody } from '@/utils/supportTasks';

import { viteAppBaseUrl } from '@/constants/env';
import { getToken } from '@/data/service/authService';
import { SupportTaskDatePicker } from './components/SupportTaskDatePicker';
import SupportTaskDropdown from './components/SupportTaskDropdown';
import SupportTaskTextField from './components/SupportTaskTextField';
import SupportTaskUploadCsvButton from './components/SupportTaskUploadCsvButton';

interface SupportTaskVisualProps {
  highlight?: boolean;
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isConsultationResponse = (response: ResponseData): response is ConsultationResponse => {
  if (!('data' in response)) {
    return false;
  }
  const { data } = response;
  return isObject(data) && 'consultation' in data && isObject(data.consultation) && 'url' in data.consultation;
};

const SupportTask = ({
  title,
  description,
  inputTextFields,
  dropdowns,
  uploadCSV,
  endpoint,
  requestType,
  highlight,
  wikiPage,
  responseType
}: SupportTasksProps & SupportTaskVisualProps) => {
  const [inputs, setInputs] = useState(() => {
    const initialState: Record<string, string> = {};
    inputTextFields?.forEach((box) => {
      initialState[box.property] = '';
    });
    dropdowns?.forEach((dropdown) => {
      initialState[dropdown.property] = dropdown.options[0]?.value || '';
    });
    return initialState;
  });
  const [file, setFile] = useState<File | null>(null);
  const [alert, setAlert] = useState<{
    show: boolean;
    severity: AlertColor | undefined;
    msg: string;
  }>({
    show: false,
    severity: 'success',
    msg: ''
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [responseUrl, setResponseUrl] = useState<string | null>(null);
  const theme = useTheme();

  const handleInputChange = (property: string, value: string | Date | null) => {
    const formatValue = value instanceof Date ? value.toISOString() : value || '';
    setInputs((prev) => ({ ...prev, [property]: formatValue }));
    setAlert({ ...alert, show: false });
    setResponseUrl(null);
  };

  const handleSubmit = async () => {
    const requiredInputs = inputTextFields?.filter((field) => !field.optional).map((field) => field.property) ?? [];
    if (requiredInputs.some((property) => !inputs[property])) {
      setAlert({ ...alert, show: true, severity: 'error', msg: 'Please fill all the required fields.' });
      return;
    }

    setLoading(true);
    setResponseUrl(null);

    try {
      const isXapiEndpoint = endpoint.includes('/api/');
      const url = isXapiEndpoint ? endpoint : `${settings.url}${endpoint}`;
      const requestData = buildJsonRequestBody(inputs, inputTextFields, dropdowns);
      const requestBody = isXapiEndpoint ? { data: requestData } : requestData;
      const data = file ? buildFormDataRequestBody(requestData, file) : requestBody;
      const contentTypeHeader = file ? 'multipart/form-data' : 'application/json';

      const res = (await axios({
        method: requestType,
        baseURL: viteAppBaseUrl(),
        url,
        data,
        headers: { 'Content-Type': contentTypeHeader, Authorization: `Bearer ${getToken()}` }
      })) as { data: ResponseData };

      if (responseType === 'url' && isConsultationResponse(res.data)) {
        setResponseUrl(res.data.data.consultation.url);
      }

      setAlert({ show: true, severity: 'success', msg: JSON.stringify(res.data, null, 2) });
    } catch (error) {
      let errorMessage = 'Unknown error';

      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data as ErrorResponse | undefined;
        if (errorData?.error?.message) {
          // Error structure for BasicErrorResponseSchema in MGS
          errorMessage = errorData.error.message;
        } else if (errorData?.message) {
          // Direct message in response
          errorMessage = errorData.message;
        } else {
          // Original axios error message (e.g. "Request failed with status code 400")
          errorMessage = error.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setResponseUrl(null);
      setAlert({ show: true, severity: 'error', msg: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const id = buildIdFromEndpoint(endpoint);

  return (
    <Paper
      key={id}
      id={id}
      data-testid="support-task-paper"
      sx={{
        p: 4,
        transition: 'box-shadow 0.3s ease-in-out',
        boxShadow: highlight ? theme.shadows[8] : theme.shadows[2]
      }}
      elevation={highlight ? 8 : 2}
    >
      <Typography variant="h5" component="h2" gutterBottom>
        {title}
      </Typography>

      {description && (
        <Typography variant="body1" gutterBottom>
          {description}
        </Typography>
      )}

      {wikiPage && (
        <Typography variant="body1" gutterBottom>
          <Link component="a" color="secondary" underline="hover" href={wikiPage} target="_blank">
            {`${title} Documentation`}
          </Link>
        </Typography>
      )}

      {alert.show && (
        <Fade in={alert.show} timeout={{ enter: 1000, exit: 1000 }}>
          <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, show: false })} sx={{ mt: 4 }}>
            <AlertTitle>{alert.severity?.toUpperCase()}</AlertTitle>
            <Box
              sx={{
                whiteSpace: 'pre-wrap'
              }}
            >
              {alert.msg}
            </Box>
          </Alert>
        </Fade>
      )}

      {responseType === 'url' && responseUrl && (
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => window.open(responseUrl, '_blank')}
          sx={{
            marginTop: (theme) => theme.spacing(4)
          }}
        >
          Open Link
        </Button>
      )}

      <Grid container spacing={4} sx={{ mt: 2 }}>
        {inputTextFields?.map((field) => (
          <Grid item xs={12} key={field.property}>
            {field.type === 'date' ? (
              <SupportTaskDatePicker
                field={field}
                value={inputs[field.property]}
                handleInputChange={handleInputChange}
              />
            ) : (
              <SupportTaskTextField
                field={field}
                value={inputs[field.property]}
                handleInputChange={handleInputChange}
              />
            )}
          </Grid>
        ))}

        {dropdowns?.map((dropdown) => (
          <Grid item xs={12} key={dropdown.property}>
            <SupportTaskDropdown
              dropdown={dropdown}
              value={inputs[dropdown.property]}
              handleInputChange={handleInputChange}
            />
          </Grid>
        ))}

        {uploadCSV && (
          <Grid item xs={12}>
            <SupportTaskUploadCsvButton file={file} setFile={setFile} />
          </Grid>
        )}
      </Grid>

      <Button
        variant="contained"
        color="secondary"
        size="large"
        onClick={handleSubmit}
        disabled={loading}
        sx={{ my: 4 }}
      >
        {loading ? <CircularProgress size={24} data-testid="CircularProgress" /> : 'Submit'}
      </Button>
    </Paper>
  );
};

export default SupportTask;
